import { MomentEditorAction } from '@/redux/actions/wechatMoment/constant';
import { renderIntlLabel } from '@/utils';

// 编辑场景
export enum EditSceneType {
  TaskTemplate, // 任务模板
  TaskDetail, // 任务详情
}

// 朋友圈文案类型
export enum MomentTextType {
  Template = 1, // 模板
  Custom = 2, // 自定义
}

// 朋友圈图片信息
export interface MomentImageInfo {
  indexId: string; // 索引id
  imageSrc: string; // 图片地址
}

// 房型营销配置
export interface RoomMarketingInfo {
  indexId: string; // 索引
  roomTypeId: number; // 房型id
  displayOption: (number | string)[]; // 展示信息
}

// 选项
interface SelectOptionInfo {
  value: number; // 选项值
  label: string; // 选项名
}

interface MarketingConfigOption extends SelectOptionInfo {
  displayName: string; // 展示名称
}

// 定时类型枚举
export enum PublishTriggerType {
  PresetTime = 1, // 定时触发
  RoomLeftCount = 2, // 空房触发
  PriceDiscount = 3, // 折扣触发
}

// 定时预设时间
export interface PresetTimeInfo {
  indexId: string; // 索引id
  timeValue: string; // 时间值
}

// 定时类型名称
export const TriggerTypeName = {
  [PublishTriggerType.PresetTime]: renderIntlLabel('wechat_publish_trigger_preset_time'),
  [PublishTriggerType.RoomLeftCount]: renderIntlLabel('wechat_publish_trigger_room_left_count'),
  [PublishTriggerType.PriceDiscount]: renderIntlLabel('wechat_publish_trigger_price_discount'),
};

export interface MainState {
  sceneType: EditSceneType; // 编辑场景
  imageList: MomentImageInfo[]; // 图片
  momentTextType: MomentTextType; // 文案类型
  headerTextStatus: boolean; // 首句文案是否开启
  headerTextContent?: string; // 首句文案
  roomMarketingList: RoomMarketingInfo[]; // 房型营销信息
  footerTextStatus: boolean; // 尾句文案是否开启
  footerTextContent?: string; // 尾句文案
  customTextContent?: string; // 自定义文案
  publishTriggerId?: number; // 触发器id
  publishTriggerType: PublishTriggerType; // 触发类型
  triggerTimeList?: PresetTimeInfo[]; // 定时预设时间列表
  triggerLeftRoomTypeId?: number; // 剩余房型id
  triggerLeftRoomTypeCount?: number; // 剩余房型数量
  triggerDiscountRoomTypeId?: number; // 折扣房型id
  triggerDiscountPriceValue?: string; // 折扣价格
  publishDelayStatus: boolean; // 发布延迟是否开启
  publishDelayTime?: number; // 发布延迟时间
}

export interface ReducerState extends MainState {
  isInit: boolean; // 初始化
  isLoading: boolean; // 加载中
  roomTypeList: SelectOptionInfo[]; // 所有房型列表
  roomMarketingOption: MarketingConfigOption[]; // 房型营销选项
}

const initialState: Partial<ReducerState> = {
  isInit: false,
  isLoading: true,
  sceneType: EditSceneType.TaskTemplate,
  roomTypeList: [],
  roomMarketingOption: [],
  imageList: [],
  headerTextStatus: false,
  roomMarketingList: [],
  footerTextStatus: false,
  publishTriggerType: PublishTriggerType.PresetTime,
  triggerTimeList: [],
  publishDelayStatus: false,
};

// action handler
type ActionHandler<T> = (state: ReducerState, action: { payload: T }) => ReducerState;
const reducerMap = {
  [MomentEditorAction.initDefaultState]: <ActionHandler<Partial<ReducerState>>>((
    state,
    { payload },
  ) => {
    return { ...state, ...payload };
  }),
  [MomentEditorAction.setLoadingStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isLoading: payload };
  }),
  [MomentEditorAction.setInitStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, isInit: payload };
  }),
  [MomentEditorAction.setRoomTypeList]: <ActionHandler<SelectOptionInfo[]>>((
    state,
    { payload },
  ) => {
    return { ...state, roomTypeList: payload };
  }),
  [MomentEditorAction.setRoomMarketingOption]: <ActionHandler<MarketingConfigOption[]>>((
    state,
    { payload },
  ) => {
    return { ...state, roomMarketingOption: payload };
  }),
  [MomentEditorAction.updateImageList]: <ActionHandler<MomentImageInfo[]>>((state, { payload }) => {
    return { ...state, imageList: payload };
  }),
  [MomentEditorAction.setTextType]: <ActionHandler<MomentTextType>>((state, { payload }) => {
    return { ...state, momentTextType: payload };
  }),
  [MomentEditorAction.setHeaderTextStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, headerTextStatus: payload };
  }),
  [MomentEditorAction.setHeaderTextContent]: <ActionHandler<string>>((state, { payload }) => {
    return { ...state, headerTextContent: payload };
  }),
  [MomentEditorAction.updateMarketingList]: <ActionHandler<RoomMarketingInfo[]>>((
    state,
    { payload },
  ) => {
    return { ...state, roomMarketingList: payload };
  }),
  [MomentEditorAction.setFooterTextStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, footerTextStatus: payload };
  }),
  [MomentEditorAction.setFooterTextContent]: <ActionHandler<string>>((state, { payload }) => {
    return { ...state, footerTextContent: payload };
  }),
  [MomentEditorAction.setCustomTextContent]: <ActionHandler<string>>((state, { payload }) => {
    return { ...state, customTextContent: payload };
  }),
  [MomentEditorAction.setPublishTriggerType]: <ActionHandler<PublishTriggerType>>((
    state,
    { payload },
  ) => {
    return { ...state, publishTriggerType: payload };
  }),
  [MomentEditorAction.updateTriggerPresetTime]: <ActionHandler<PresetTimeInfo[]>>((
    state,
    { payload },
  ) => {
    return { ...state, triggerTimeList: payload };
  }),
  [MomentEditorAction.setTriggerLeftId]: <ActionHandler<number>>((state, { payload }) => {
    return { ...state, triggerLeftRoomTypeId: payload };
  }),
  [MomentEditorAction.setTriggerLeftValue]: <ActionHandler<number>>((state, { payload }) => {
    return { ...state, triggerLeftRoomTypeCount: payload };
  }),
  [MomentEditorAction.setTriggerDiscountId]: <ActionHandler<number>>((state, { payload }) => {
    return { ...state, triggerDiscountRoomTypeId: payload };
  }),
  [MomentEditorAction.setTriggerDiscountValue]: <ActionHandler<string>>((state, { payload }) => {
    return { ...state, triggerDiscountPriceValue: payload };
  }),
  [MomentEditorAction.setPublishDelayStatus]: <ActionHandler<boolean>>((state, { payload }) => {
    return { ...state, publishDelayStatus: payload };
  }),
  [MomentEditorAction.setPublishDelayTime]: <ActionHandler<number>>((state, { payload }) => {
    return { ...state, publishDelayTime: payload };
  }),
};

const reducerMapper = (state = initialState, action) => {
  const actionType = action.type;
  const reducer = reducerMap[actionType];
  if (typeof reducer === 'function') {
    return reducer(<ReducerState>state, action);
  }
  return state;
};

export const storeName = 'wechatMomentEditor';

export default reducerMapper;
